
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import AppLayout from '@/components/app/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, CreditCard, Star, ArrowLeft, Calendar } from 'lucide-react';

const TreatmentDetail = () => {
  const { slug } = useParams();

  // Mock treatment data - in real app this would be fetched based on slug
  const treatment = {
    name: "AquaFirme Facial",
    credits: 4,
    duration: "30 Min",
    description: "Exfoliate + hydrate in 30 minutes. Our signature AquaFirme treatment combines gentle exfoliation with deep hydration, leaving your skin feeling refreshed and glowing.",
    longDescription: "The AquaFirme Facial is our most popular treatment, designed to give you instant results with zero downtime. Using advanced hydrafacial technology, we cleanse, exfoliate, and infuse your skin with nourishing serums tailored to your specific needs.",
    benefits: [
      "Deep cleansing and exfoliation",
      "Immediate hydration boost", 
      "Improved skin texture and tone",
      "No downtime required",
      "Suitable for all skin types"
    ],
    image: "💧",
    category: "Skin",
    tier: "Essential",
    beforeAfter: true,
    bundle: {
      title: "Bundle with LipLase for 2 credits off",
      description: "Combine your facial with our lip enhancement treatment",
      totalCredits: 6,
      savings: 2
    }
  };

  return (
    <AppLayout>
      <div className="px-4 py-6 space-y-6">
        {/* Back Navigation */}
        <div className="flex items-center gap-3">
          <Button asChild variant="outline" size="sm">
            <Link to="/app/treatments">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Treatments
            </Link>
          </Button>
        </div>

        {/* Hero Section */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-juv-soft-blush to-juv-rose-clay p-8 text-center">
            <div className="text-6xl mb-4">{treatment.image}</div>
            <h1 className="text-2xl font-bold text-juv-deep-chestnut mb-2">{treatment.name}</h1>
            <div className="flex items-center justify-center gap-4 mb-4">
              <span className="bg-white px-3 py-1 rounded-full text-sm font-medium text-juv-deep-chestnut">
                {treatment.category} · {treatment.tier}
              </span>
              <span className="flex items-center gap-1 text-juv-deep-chestnut">
                <Clock className="w-4 h-4" />
                {treatment.duration}
              </span>
            </div>
            <p className="text-gray-700 mb-6">{treatment.description}</p>
            
            {/* Credit Cost & CTA */}
            <div className="bg-white rounded-lg p-4 inline-block">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-5 h-5 text-juv-terracotta" />
                <span className="text-2xl font-bold text-juv-terracotta">{treatment.credits} Credits</span>
              </div>
              <div className="flex gap-3">
                <Button className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
                  Use My Credits
                </Button>
                <Button asChild variant="outline">
                  <Link to="/app/book">
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Now
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Treatment Details */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-juv-deep-chestnut mb-4">What to Expect</h2>
            <p className="text-gray-600 mb-6">{treatment.longDescription}</p>
            
            <h3 className="font-semibold text-juv-deep-chestnut mb-3">Treatment Benefits</h3>
            <ul className="space-y-2">
              {treatment.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Star className="w-4 h-4 text-juv-terracotta mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">{benefit}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Bundle Offer */}
        {treatment.bundle && (
          <Card className="bg-gradient-to-r from-juv-soft-blush to-juv-rose-clay">
            <CardContent className="p-6">
              <h3 className="font-semibold text-juv-deep-chestnut mb-2">{treatment.bundle.title}</h3>
              <p className="text-gray-600 mb-4">{treatment.bundle.description}</p>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-lg font-semibold text-juv-terracotta">
                    {treatment.bundle.totalCredits} Credits
                  </span>
                  <span className="text-sm text-gray-600 ml-2">
                    (Save {treatment.bundle.savings} credits)
                  </span>
                </div>
                <Button variant="outline" className="border-juv-terracotta text-juv-terracotta">
                  Book Bundle
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Before & After Gallery */}
        {treatment.beforeAfter && (
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-juv-deep-chestnut mb-4">Real Results</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-100 rounded-lg aspect-square flex items-center justify-center">
                  <span className="text-gray-500">Before Photo</span>
                </div>
                <div className="bg-juv-soft-blush rounded-lg aspect-square flex items-center justify-center">
                  <span className="text-juv-deep-chestnut">After Photo</span>
                </div>
              </div>
              <p className="text-center text-sm text-gray-600 mt-3">
                Results from a single AquaFirme treatment
              </p>
            </CardContent>
          </Card>
        )}

        {/* Treatment Notes */}
        <Card className="border-juv-rose-clay border">
          <CardContent className="p-4">
            <h4 className="font-semibold text-juv-deep-chestnut mb-2">Important Notes</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Credit-based pricing visible for logged-in members only</li>
              <li>• Treatment includes consultation and aftercare instructions</li>
              <li>• Results may vary based on individual skin condition</li>
              <li>• Free consultation available if you're unsure about treatment</li>
            </ul>
          </CardContent>
        </Card>

        {/* Related Treatments */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-juv-deep-chestnut mb-4">You Might Also Like</h3>
            <div className="grid grid-cols-1 gap-3">
              {[
                { name: "Dermaplaning Add-on", credits: 2, duration: "15 min" },
                { name: "LED Light Therapy", credits: 1, duration: "20 min" },
                { name: "Vitamin C Infusion", credits: 1, duration: "10 min" }
              ].map((addon, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-juv-deep-chestnut">{addon.name}</p>
                    <p className="text-sm text-gray-600">{addon.duration}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-juv-terracotta font-medium">+{addon.credits} Credits</p>
                    <Button size="sm" variant="outline" className="mt-1">Add</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default TreatmentDetail;
